/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { View, StyleSheet, Platform, Dimensions } from "react-native";
import ProbationaryReviewCard from "../ProbationaryReviewCard";
import PreferredMethodChecklist from "../PreferredMethodChecklist";
import { TOUCHPOINT_DISPLAY_NAMES } from "../../../../constants/touchpoints";
import { useTheme } from "../../../../theme";

interface ProbationData {
  probation: {
    id: number;
    probation_id: string;
    gpid: string;
    location: string;
    type_of_touchpoint: string;
  };
  data: Array<{
    Data_Id: number;
    Response: number; // 1: Yes, 2: No, 3: N/A
    Section: number;
  }>;
}

export interface ProbationReviewData {
  "Preferred Method": null | any;
  "Milestone 1": ProbationData | null;
  "Milestone 2": ProbationData | null;
  "Milestone 3": ProbationData | null;
  // Legacy support for existing API data
  "Probation 30"?: ProbationData | null;
  "Probation 60"?: ProbationData | null; 
  "Probation 90"?: ProbationData | null;
}

interface ProbationaryReviewSectionProps {
  probationData: ProbationReviewData;
  selectedMethods?: string[];
  onSelectMethod?: (method: string) => void;
}

const calculateResponseCounts = (data: ProbationData | null) => {
  console.log(
    "Calculating response counts for:",
    data?.probation?.type_of_touchpoint || "N/A"
  );
  console.log("Raw data:", JSON.stringify(data, null, 2));

  if (!data?.data || !Array.isArray(data.data) || data.data.length === 0) {
    console.log("No valid data array available");
    return { yesCount: 0, noCount: 0, naCount: 0 };
  }

  // Filter out any invalid responses
  const validData = data.data.filter(
    (item) => item && typeof item.Response === "number"
  );

  if (validData.length === 0) {
    console.log("No valid responses found in data");
    return { yesCount: 0, noCount: 0, naCount: 0 };
  }

  const counts = {
    yesCount: validData.filter((item) => item.Response === 1).length,
    noCount: validData.filter((item) => item.Response === 2).length,
    naCount: validData.filter((item) => item.Response === 3).length,
  };

  console.log("Calculated counts:", counts);
  return counts;
};

const ProbationaryReviewSection: React.FC<ProbationaryReviewSectionProps> = ({
  probationData,
  selectedMethods = [],
  onSelectMethod = () => {},
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  console.log("Received probation data:", probationData);

  const methods = ["Email", "Phone", "In-person", "Video Call", "Chat"];

  // Process counts for each probation period
  // Calculate counts for probationary reviews - support both new and legacy keys
  const milestone1Data = probationData?.["Milestone 1"] || probationData?.["Probation 30"];
  const milestone2Data = probationData?.["Milestone 2"] || probationData?.["Probation 60"];
  const milestone3Data = probationData?.["Milestone 3"] || probationData?.["Probation 90"];
  
  const milestone1Counts = calculateResponseCounts(milestone1Data);
  const milestone2Counts = calculateResponseCounts(milestone2Data);
  const milestone3Counts = calculateResponseCounts(milestone3Data);

  // Calculate preferred method counts
  const preferredMethodCounts = {
    yesCount:
      probationData?.["Preferred Method"]?.data?.filter(
        (item: { Response: number }) => item.Response === 1
      )?.length || 0,
    noCount:
      probationData?.["Preferred Method"]?.data?.filter(
        (item: { Response: number }) => item.Response === 2
      )?.length || 0,
    naCount:
      probationData?.["Preferred Method"]?.data?.filter(
        (item: { Response: number }) => item.Response === 3
      )?.length || 0,
  };

  console.log("Final review counts:", {
    "Milestone 1": milestone1Counts,
    "Milestone 2": milestone2Counts,
    "Milestone 3": milestone3Counts,
    "Preferred Method": preferredMethodCounts,
  });

  // Check if device is iOS
  const isIOS = Platform.OS === "ios";
  // Check if iPad - iOS device with width > 768
  const isIPad = isIOS && Dimensions.get("window").width > 768;
  // Check if iPhone - iOS device with width <= 768
  const isIPhone = isIOS && Dimensions.get("window").width <= 768;

  // For iPad layout, PreferredMethodChecklist should be beside 90-day card
  if (isIPad) {
    return (
      <View
        style={[
          styles.probationaryReviewContainer,
          styles.iosReviewContainer,
          isDarkTheme && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
            borderWidth: 1,
          },
        ]}
      >
        {/* First row - 30-day and 60-day cards */}
        <View style={styles.ipadRow}>
          <View style={styles.ipadCardContainer}>
            <ProbationaryReviewCard
              milestoneNumber={1}
              yesCount={milestone1Counts.yesCount}
              noCount={milestone1Counts.noCount}
              naCount={milestone1Counts.naCount}
              status={milestone1Data ? "completed" : "pending"}
            />
          </View>
          <View style={styles.ipadCardContainer}>
            <ProbationaryReviewCard
              milestoneNumber={2}
              yesCount={milestone2Counts.yesCount}
              noCount={milestone2Counts.noCount}
              naCount={milestone2Counts.naCount}
              status={milestone2Data ? "completed" : "pending"}
            />
          </View>
        </View>

        {/* Second row - 90-day card and PreferredMethodChecklist */}
        <View style={styles.ipadRow}>
          <View style={styles.ipadCardContainer}>
            <ProbationaryReviewCard
              milestoneNumber={3}
              yesCount={milestone3Counts.yesCount}
              noCount={milestone3Counts.noCount}
              naCount={milestone3Counts.naCount}
              status={milestone3Data ? "completed" : "pending"}
            />
          </View>
          <View style={styles.ipadCardContainer}>
            <PreferredMethodChecklist
              width="100%"
              yesCount={preferredMethodCounts.yesCount}
              noCount={preferredMethodCounts.noCount}
              naCount={preferredMethodCounts.naCount}
            />
          </View>
        </View>
      </View>
    );
  }

  // For iPhone or web layouts
  return (
    <View
      style={[
        styles.probationaryReviewContainer,
        isIOS && styles.iosReviewContainer,
        isIPhone && styles.iphoneReviewContainer,
        isDarkTheme && {
          backgroundColor: colors.background.surface,
          borderColor: colors.border.default,
          borderWidth: 1,
        },
      ]}
    >
      <View
        style={[styles.cardContainer, isIPhone && styles.iphoneCardContainer]}
      >
        <ProbationaryReviewCard
          milestoneNumber={1}
          yesCount={milestone1Counts.yesCount}
          noCount={milestone1Counts.noCount}
          naCount={milestone1Counts.naCount}
          status={milestone1Data ? "completed" : "pending"}
        />
      </View>
      <View
        style={[styles.cardContainer, isIPhone && styles.iphoneCardContainer]}
      >
        <ProbationaryReviewCard
          milestoneNumber={2}
          yesCount={milestone2Counts.yesCount}
          noCount={milestone2Counts.noCount}
          naCount={milestone2Counts.naCount}
          status={milestone2Data ? "completed" : "pending"}
        />
      </View>
      <View
        style={[styles.cardContainer, isIPhone && styles.iphoneCardContainer]}
      >
        <ProbationaryReviewCard
          milestoneNumber={3}
          yesCount={milestone3Counts.yesCount}
          noCount={milestone3Counts.noCount}
          naCount={milestone3Counts.naCount}
          status={milestone3Data ? "completed" : "pending"}
        />
      </View>
      <View
        style={[styles.cardContainer, isIPhone && styles.iphoneCardContainer]}
      >
        <PreferredMethodChecklist
          width="100%"
          yesCount={preferredMethodCounts.yesCount}
          noCount={preferredMethodCounts.noCount}
          naCount={preferredMethodCounts.naCount}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  probationaryReviewContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 0,
    //paddingHorizontal: Platform.OS === "ios" ? 20 : 20,
    marginVertical: 20,
    //backgroundColor: "white",
  },
  iosReviewContainer: {
    backgroundColor: "#f8f9fa",
    borderRadius: 12,
    marginHorizontal: 16,
    marginVertical: 20,
    paddingVertical: 15,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  iphoneReviewContainer: {
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "#f8f9fa",
  },
  cardContainer: {
    width: "24%", // Default width for web (4 cards per row)
    marginVertical: 0, // Override the component internal margins
    ...(Platform.OS === "web" && {
      paddingHorizontal: 10, // Add some spacing for web layout
    }),
  },
  ipadRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 15,
  },
  ipadCardContainer: {
    width: "48%", // iPad shows 2 cards per row
    paddingHorizontal: 5,
    height: "auto", // Let content determine height
  },
  iphoneCardContainer: {
    width: "100%", // iPhone shows 1 card per row
    marginBottom: 15,
    paddingHorizontal: 5,
  },
  iosPreferredMethodContainer: {
    width: "100%",
    marginTop: 10,
    paddingHorizontal: 5,
  },
});

export default ProbationaryReviewSection;
